import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useFormik } from 'formik'
import { userProfileDetails, updateUserProfile } from '../../../services/profile.services'
import CustomError from '../../../../../utils/customError'
import Swal from 'sweetalert2'
import { SWAL_SETTINGS, INPUT_MAX_25, PHONE_NO_LENGTH, LOADER_TIMEOUT_TIME, DOCTOR_SPECIALIZATION, INPUT_MAX_50, ROLE } from '../../../../../utils/Constants'
import { handleServerValidations, DT, globalLoader, getDateWithTime, capitalizeFirstLetter } from '../../../../../utils/commonfunction'
import * as Yup from 'yup'
import PhoneInput from '../../../../../utils/PhoneInput'
import Skeleton from 'react-loading-skeleton'
import { DatePicker, TagPicker } from 'rsuite'
import { SpecializationList } from '../../../../admin/services/doctor-specialization.services'

export default function DoctorMyProfile() {
    const { t } = useTranslation()
    const [defaultData, setDefaultData] = useState({});
    const fileSelectInput = useRef();
    const [loader, setLoader] = useState(true);
    const [addSubmissionLoader, setAddSubmissionLoader] = useState(false);
    const SUPPORTED_FORMATS = ["image/jpg", "image/jpeg", "image/png", "image/gif", "image/webp"];
    const [specialization, setSpecialization] = useState([]);

    const validateSchema = Yup.object().shape({
        first_name: Yup.string().trim().max(INPUT_MAX_50, "Max allowed length of characters is 50").required("center.profile.label_first_name_error"),
        last_name: Yup.string().trim().max(INPUT_MAX_50, "Max allowed length of characters is 50").required("center.profile.label_last_name_error"),
        email: Yup.string()
            .trim()
            .email("center.profile.label_email_invalid_format_error")
            .test('no-uppercase', "center.profile.label_email_uppercase_error", function (value) {
                if (value) {
                    return !/[A-Z]/.test(value);
                }
                return true;
            })
            .required("center.profile.label_email_required_error"),
        phone_number: Yup.string()
            .test('phone_required', 'center.profile.label_phone_number_required_error', (value) => {
                const phoneNumber = (value.split(" "))[1];
                return phoneNumber && phoneNumber.length;
            }
            )
            .test('phone_digit_error', 'center.profile.label_phone_number_digits_error', (value) => {
                const phoneNumber = (value.split(" "))[1];
                return phoneNumber && /^\d+$/.test(phoneNumber);
            }
            )
            .test('phone_min_length', 'center.profile.label_phone_number_min_length_error', (value) => {
                const phoneNumber = (value.split(" "))[1];
                return phoneNumber && phoneNumber.length >= PHONE_NO_LENGTH.min;
            }
            )
            .test('phone_max_length', 'center.profile.label_phone_number_max_length_error', (value) => {
                const phoneNumber = (value.split(" "))[1];
                return phoneNumber && phoneNumber.length <= PHONE_NO_LENGTH.max;
            }
            )
        ,
        // address: Yup.string().max(TEXTAREA_MAX_LENGTH, DT("restaurant.profile.validation_max_input_characters", [TEXTAREA_MAX_LENGTH])).trim().required("restaurant.profile.label_location_error"),
        // profile_image: Yup.string().required("restaurant.profile.label_profile_image_error"),
        profile_image: Yup.mixed()
            .nullable()
            .test(
                'fileOrUrl',
                'admin.cafes.supported_file_error',
                value => {
                    if (!value) return true;
                    if (typeof value === 'string') return /^(http|https):\/\/[^\s$.?#].[^\s]*$/gm.test(value);
                    return ['image/jpg', 'image/jpeg', 'image/png', 'image/webp', 'image/gif'].includes(value.type);
                }
            )
            .test(
                'fileSize',
                'admin.cafes.image_max_size_error',
                value => {
                    if (!value || typeof value === 'string') return true;
                    return value.size <= 1.5 * 1024 * 1024;
                }
            ),
        // specialization: Yup.string().required("Specialization is required"),

        specialization: Yup.mixed()
            .required("Specialization is required")
            .test("no-spaces", "Specialization is required", (value) => {
                return String(value).trim().length > 0;
            }),

        startTime: Yup.string().required('center.cafe_profile.opening_time_error'),
        endTime: Yup.string()
            .required('center.cafe_profile.closing_time_error')
            .test(
                'not_equal_time',
                'Start time and end time cannot be the same.',
                function (endTime) {
                    const { startTime } = this.parent;
                    if (!startTime || !endTime) return true;

                    return endTime !== startTime;
                }
            )
            .test(
                'greater_time',
                'center.cafe_profile.rest_validation_closing_time_should_be_greater_than_opening_time',
                function (endTime) {
                    const { startTime } = this.parent;
                    if (!startTime || !endTime) return true;

                    return endTime > startTime;
                }
            ),
    })

    useEffect(() => {
        userProfileDetails().then((response) => {
            setTimeout(() => { setLoader(false); }, LOADER_TIMEOUT_TIME);
            setDefaultData(response && response.data ? response?.data : {});
        }).catch((error) => {
            setTimeout(() => { setLoader(false); }, LOADER_TIMEOUT_TIME);
            Swal.fire({
                icon: "error",
                text: handleServerValidations(error),
                ...SWAL_SETTINGS,
            });
        })
    }, []);

    useEffect(() => {
        SpecializationList(ROLE.DOCTOR)
            .then((data) => {
                setSpecialization(data && data.data ? data.data.list : []);
            })
            .catch((error) => {
                console.log("error ====> ", error);
            });
    }, []);


    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            email: defaultData?.email ?? "",
            first_name: defaultData?.first_name ?? "",
            last_name: defaultData?.last_name ?? "",
            // address: defaultData?.address ?? "",
            phone_number: defaultData?.phone_number ?? "",
            // phone_code: defaultData?.phone_code ?? "",
            profile_image: defaultData?.profile_image ?? "",
            specialization: defaultData?.specialisation ?? [],
            qualification1: defaultData?.qualification1 ?? "",
            qualification2: defaultData?.qualification2 ?? "",
            institute1: defaultData?.institute1 ?? "",
            institute2: defaultData?.institute2 ?? "",
            consultation_charge: defaultData?.consultation_charge ?? "",
            startTime: defaultData && defaultData?.centers && defaultData?.centers?.length > 0
                ? defaultData?.centers[0]?.start_available_time
                : "",
            endTime: defaultData && defaultData?.centers && defaultData?.centers?.length > 0
                ? defaultData.centers[0]?.end_available_time
                : "",
            center: defaultData && defaultData?.centers && defaultData?.centers?.length > 0
                ? defaultData.centers[0]?.center?.name
                : ""
        },
        validationSchema: validateSchema,
        onSubmit: (values, { setSubmitting, resetForm }) => {
            // globalLoader(true);
            let formData = new FormData();
            formData.append("email", values.email);
            formData.append("first_name", values.first_name);
            formData.append("last_name", values.last_name);
            // formData.append("address", values.address);
            formData.append("phone_number", values.phone_number);
            formData.append("profile_image", values.profile_image);
            formData.append("specialisation", JSON.stringify(values.specialization));
            formData.append("start_available_time", values.startTime);
            formData.append("end_available_time", values.endTime);
            formData.append("center_name", values.center);
            formData.append("qualification1", values.qualification1);
            formData.append("qualification2", values.qualification2);
            formData.append("institute1", values.institute1);
            formData.append("institute2", values.institute2);
            setAddSubmissionLoader(true);

            updateUserProfile(formData)
                .then((response) => {
                    setAddSubmissionLoader(false);
                    globalLoader(false)
                    setSubmitting(false);
                    if (response.success) {
                        Swal.fire({
                            icon: "success",
                            text: response.message,
                            ...SWAL_SETTINGS,
                        });
                    } else {
                        Swal.fire({
                            icon: "error",
                            text: handleServerValidations(response),
                            ...SWAL_SETTINGS,
                        });
                    }
                })
                .catch((error) => {
                    setAddSubmissionLoader(false);
                    globalLoader(false)
                    Swal.fire({
                        icon: "error",
                        text: handleServerValidations(error),
                        ...SWAL_SETTINGS,
                    });
                    console.log("error===>", error)
                })
        }
    });

    const handleFileSelection = (event) => {
        if (event.target.files.length) {
            formik.setFieldValue("profile_image", event.target.files[0]);
        }
    };

    const handleFilePreview = (file) => {
        if (typeof file === "string") {
            return file;
        } else {
            return URL.createObjectURL(file);
        }
    };

    const specializationData = specialization.map((item) => ({
        label: capitalizeFirstLetter(item?.title),
        value: item?._id,
    }));


    return (
        <main id='main-content'>
            <div className='inner-container'>
                <ul class="nav nav-pills mb-3" id="pills-tab" role="tablist">
                    <li className="nav-item">
                        <Link className="nav-link active" to={'/doctor/profile'}>{t("My Profile")}</Link>
                    </li>
                    <li className="nav-item">
                        <Link className="nav-link" to={'/doctor/profile/change-password'}>{t("Change Password")}</Link>
                    </li>
                </ul>
                <div className='card'>
                    <div className='card-body'>
                        {loader ?
                            <div className='w-100'>
                                <Skeleton circle={true} style={{ width: "96px", height: "96px" }} />
                                <Skeleton className='mt-4' style={{ height: "30px" }} />
                                <Skeleton className='mt-4' style={{ height: "30px" }} />
                                <Skeleton className='mt-4' style={{ height: "30px" }} />
                            </div>
                            :
                            <form onSubmit={formik.handleSubmit}>
                                <div className='profile-photo-upload mb-3'>
                                    <div className='avatar avatar-xl'>
                                        <img alt='profile-img' src={handleFilePreview(formik?.values?.profile_image)} />
                                    </div>
                                    {/* <button className='cp e_none' type='button' onClick={() => { fileSelectInput?.current?.click() }}>
                                        <input
                                            ref={fileSelectInput}
                                            id="profile_image"
                                            name="profile_image"
                                            accept="image/*"
                                            type='file'
                                            onChange={handleFileSelection}
                                        />
                                        <i className='ti ti-upload'></i>
                                    </button> */}
                                </div>
                                <CustomError name="profile_image" form={formik} className="text-danger my-3" />
                                <div className='row'>
                                    <div className='col-md-6'>
                                        <div className="mb-3">
                                            <label htmlFor='first_name' className="form-label">{t("center.profile.label_first_name")}<span>*</span></label>
                                            <input
                                                type="text"
                                                id='first_name'
                                                className="form-control"
                                                placeholder={t("center.profile.placeholder_first_name")}
                                                name="first_name"
                                                value={formik.values.first_name}
                                                onChange={formik.handleChange}
                                                onBlur={formik.onBlur}
                                                disabled
                                            />
                                            <CustomError name="first_name" form={formik} className="text-danger" />
                                        </div>
                                    </div>
                                    <div className='col-md-6'>
                                        <div className="mb-3">
                                            <label htmlFor='last_name' className="form-label">{t("center.profile.label_last_name")}<span>*</span></label>
                                            <input
                                                type="text"
                                                id='last_name'
                                                className="form-control"
                                                placeholder={t("center.profile.placeholder_last_name")}
                                                name='last_name'
                                                value={formik.values.last_name}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                disabled
                                            />
                                            <CustomError name="last_name" form={formik} className="text-danger" />
                                        </div>
                                    </div>
                                    <div className='col-md-6'>
                                        <div className="mb-3">
                                            <label htmlFor='email' className="form-label">{t("center.profile.label_email_address")} <span>*</span></label>
                                            <input
                                                type="email"
                                                id='email'
                                                className="form-control"
                                                name='email'
                                                disabled
                                                value={formik.values.email}
                                            />
                                            <CustomError name="email" form={formik} className="text-danger" />
                                        </div>
                                    </div>
                                    <div className='col-md-6'>
                                        <div className="mb-3">
                                            <label htmlFor='phone_number' className="form-label">{t("center.profile.label_phone_number")}<span>*</span></label>
                                            <PhoneInput
                                                value={formik?.values?.phone_number}
                                                disabled
                                                name={'phone_number'} onChange={(e) => { formik.setFieldValue('phone_number', e?.phone_number) }} onBlur={formik.handleBlur} />
                                            <CustomError name="phone_number" form={formik} className="text-danger" shortCodes={{ PHONE_MIN_LENGTH: PHONE_NO_LENGTH.min, PHONE_MAX_LENGTH: PHONE_NO_LENGTH.max }} />
                                        </div>
                                    </div>
                                    <div className='col-md-6'>
                                        <div className="mb-3">
                                            <label className="form-label">{t("Specialization")}<span>*</span></label>
                                            {/* <select className="form-control cp" name='specialization' value={formik?.values?.specialization} onChange={formik.handleChange}>
                                                <option value={""}>{t("Select")}</option>
                                                {DOCTOR_SPECIALIZATION.map((item, index) => { return (<option key={index} value={item.value}>{item.label}</option>) })}
                                            </select> */}
                                            <TagPicker
                                                data={specializationData}
                                                defaultValue={formik.values.specialization}
                                                className="form-control"
                                                disabled
                                                onChange={(e) => formik.setFieldValue("specialization", e)}
                                                cleanable={false}
                                                value={formik.values.specialization}
                                            />
                                            {formik.touched.specialization && formik.errors.specialization ? (
                                                <div className="text-danger">{t(formik.errors.specialization)}</div>
                                            ) : null}
                                        </div>
                                    </div>
                                    <div className='col-md-6'>
                                        <div className="mb-3">
                                            <label htmlFor='center' className="form-label">{t("Center")}</label>
                                            <input
                                                type="text"
                                                id='center'
                                                className="form-control"
                                                placeholder={t("Enter center")}
                                                name='center'
                                                value={formik.values.center}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                disabled
                                            />
                                            {/* <CustomError name="center" form={formik} className="text-danger" /> */}
                                        </div>
                                    </div>
                                    <div className='col-md-6'>
                                        <div className="mb-3">
                                            <label htmlFor='qualification1' className="form-label">{t("Qualification 1")}</label>
                                            <input
                                                type="text"
                                                id='qualification1'
                                                className="form-control"
                                                placeholder={t("Enter qualification 1")}
                                                name='qualification1'
                                                value={formik.values.qualification1}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                disabled
                                            />
                                            {/* <CustomError name="center" form={formik} className="text-danger" /> */}
                                        </div>
                                    </div>
                                    <div className='col-md-6'>
                                        <div className="mb-3">
                                            <label htmlFor='institute1' className="form-label">{t("Institute1 1")}</label>
                                            <input
                                                type="text"
                                                id='institute1'
                                                className="form-control"
                                                placeholder={t("Enter institute 1")}
                                                name='institute1'
                                                value={formik.values.institute1}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                disabled
                                            />
                                            {/* <CustomError name="center" form={formik} className="text-danger" /> */}
                                        </div>
                                    </div>
                                    <div className='col-md-6'>
                                        <div className="mb-3">
                                            <label htmlFor='qualification2' className="form-label">{t("Qualification 2")}</label>
                                            <input
                                                type="text"
                                                id='qualification2'
                                                className="form-control"
                                                placeholder={t("Enter qualification 2")}
                                                name='qualification2'
                                                value={formik.values.qualification2}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                disabled
                                            />
                                            {/* <CustomError name="center" form={formik} className="text-danger" /> */}
                                        </div>
                                    </div>
                                    <div className='col-md-6'>
                                        <div className="mb-3">
                                            <label htmlFor='institute2' className="form-label">{t("Institute 2")}</label>
                                            <input
                                                type="text"
                                                id='institute2'
                                                className="form-control"
                                                placeholder={t("Enter institute 2")}
                                                name='institute2'
                                                value={formik.values.institute2}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                disabled
                                            />
                                            {/* <CustomError name="center" form={formik} className="text-danger" /> */}

                                        </div>
                                    </div>
                                    <div className='col-md-6'>
                                        <div className="mb-3">
                                            <label htmlFor='consultation_charge' className="form-label">{t("Consultation Charge")}</label>
                                            <input
                                                type="text"
                                                id='consultation_charge'
                                                className="form-control"
                                                placeholder={t("Enter consultation charge")}
                                                name='consultation_charge'
                                                value={formik.values.consultation_charge}
                                                onChange={formik.handleChange}
                                                onBlur={formik.handleBlur}
                                                disabled
                                            />
                                            {/* <CustomError name="center" form={formik} className="text-danger" /> */}
                                        </div>
                                    </div>
                                    <div className="col-md-6">
                                        <div className="row">
                                            <div className="col-md-12">
                                                <label className="form-label">
                                                    Business Hours
                                                    <span>*</span>
                                                </label>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="mb-3">
                                                    <DatePicker 
                                                        classPrefix="restaurant" 
                                                        placement="autoVertical" 
                                                        format="HH:mm" 
                                                        cleanable={false} 
                                                        editable={true} 
                                                        name="startTime" 
                                                        disabled 
                                                        onChange={(e) => {
                                                            formik.setFieldTouched('startTime', true);
                                                            const match = String(e).match(/\d{2}:\d{2}:\d{2}/);
                                                            if (match) {
                                                                const [hours, minutes] = match[0].split(':');
                                                                formik.setFieldValue("startTime", `${hours}:${minutes}:00`);
                                                            }
                                                        }} 
                                                        value={getDateWithTime(formik.values.startTime)}
                                                        onBlur={formik.handleBlur}
                                                        hideMinutes={(minute) => minute % 15 !== 0}
                                                    />
                                                    <CustomError name="startTime" form={formik} className="text-danger" />
                                                </div>
                                            </div>
                                            <div className="col-md-6">
                                                <div className="mb-3 ms-4">
                                                    <DatePicker 
                                                        classPrefix="restaurant" 
                                                        placement="autoVertical" 
                                                        format="HH:mm" 
                                                        cleanable={false} 
                                                        editable={true} 
                                                        name="endTime" 
                                                        disabled 
                                                        onChange={(e) => {
                                                            formik.setFieldTouched('endTime', true);
                                                            const match = String(e).match(/\d{2}:\d{2}:\d{2}/);
                                                            if (match) {
                                                                const [hours, minutes] = match[0].split(':');
                                                                formik.setFieldValue("endTime", `${hours}:${minutes}:00`);
                                                            }
                                                        }} 
                                                        value={getDateWithTime(formik.values.endTime)}
                                                        hideMinutes={(minute) => minute % 15 !== 0}
                                                    />
                                                    <CustomError name="endTime" form={formik} className="text-danger" />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                {/* <div className='button-form'>
                                    <button className={"btn btn-primary " + (addSubmissionLoader ? " btn-loader" : "")}>{t("center.profile.btn_update_profile")}</button>
                                </div> */}
                            </form>
                        }
                    </div>
                </div>
            </div>
        </main>
    )
}
